import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import React, { useCallback, useRef } from 'react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useGetApiV1ClientSlidersQuery } from 'service/api/allApis';
import { Skeleton } from 'components/common/skeleton';
import { useLanguage } from 'hooks/use-language';
import { cn } from 'lib/utils';
import { useNavigate } from 'react-router-dom';

const LandingSlider = () => {
    const { data, isLoading } = useGetApiV1ClientSlidersQuery({
        isPaginated: false,
    });
    const { selectedLanguageId } = useLanguage();
    const navigate = useNavigate();

    const sliderRef = useRef<any>(null);

    const handlePrev = useCallback(() => {
        if (!sliderRef.current) return;
        sliderRef.current.swiper.slidePrev();
    }, []);

    const handleNext = useCallback(() => {
        if (!sliderRef.current) return;
        sliderRef.current.swiper.slideNext();
    }, []);

    return (
        <Swiper
            loop
            ref={sliderRef}
            className="w-full duration-150 m-0 relative"
            autoplay={{
                delay: 5000,
                disableOnInteraction: false,
            }}
            effect="fade"
            fadeEffect={{
                crossFade: true,
            }}
            modules={[EffectFade, Navigation, Pagination, Autoplay]}
        >
            {data?.items?.map((slide, idx) => {
                const item = slide?.sliderDictionaryGetAllDtos?.find(
                    (i) => i.languageId === selectedLanguageId,
                );
                if (!item) return null;
                const { image, redirectUrl } = slide;
                const { title, description } = item;
                return (
                    <SwiperSlide
                        key={idx}
                        className={cn('px-5', redirectUrl && 'cursor-pointer')}
                        onClick={() => {
                            if (!redirectUrl) return;
                            const url = new URL(redirectUrl);

                            if (window.location.hostname === url.hostname) {
                                navigate(url.pathname);
                                return;
                            }
                            window.location.href = redirectUrl;
                        }}
                    >
                        <h1 className="text-txt md:text-2xl font-bold tracking-[1.92px] whitespace-nowrap ">
                            {title}
                        </h1>
                        <p className="text-dark-60 text-[15px] leading-5 mt-3 mb-4 line-clamp-4 min-h-[80px]">
                            {description}
                        </p>
                        <img
                            src={image ?? ''}
                            alt="Slide"
                            className="w-full md:h-[275px] lg:h-[300px] object-cover rounded-[15px]"
                            onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/not-found.png';
                            }}
                        />
                    </SwiperSlide>
                );
            })}
            {isLoading && (
                <SwiperSlide className="px-4 w-full z-[50]">
                    <Skeleton className="w-[360px] h-6 md:h-8 ml-1" />
                    <Skeleton className="w-[calc(100%-8px)] mx-auto h-[80px] mt-3" />
                    <Skeleton className="w-full aspect-video md:w-[485px] lg:w-[560px] md:h-[275px] lg:h-[300px] mt-4 ml-1 rounded-[15px]" />
                </SwiperSlide>
            )}
            <button
                className="absolute left-[2px] bottom-[calc(50%-80px)] md:bottom-[118px] lg:bottom-[130px] z-10 w-10 h-10 bg-white rounded-full grid place-items-center border border-transparent hover:border-primary duration-100"
                style={{
                    boxShadow:
                        'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
                onClick={handlePrev}
            >
                <ArrowLeftIcon className="mt-0.5 mr-0.5" />
            </button>
            <button
                className="absolute right-[2px] bottom-[calc(50%-80px)] md:bottom-[118px] lg:bottom-[130px] z-10 w-10 h-10 bg-white rounded-full grid place-items-center border border-transparent hover:border-primary duration-100"
                style={{
                    boxShadow:
                        'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
                onClick={handleNext}
            >
                <ArrowLeftIcon className="mt-0.5 ml-1 rotate-180" />
            </button>
        </Swiper>
    );
};

export default LandingSlider;
